import { FormikField } from "@components/forms/FormikField/FormikField";
import { CountInput } from "@design-system/CountInput/CountInput";
import type { InputSize } from "@helpers/form";
import type { FieldPath } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import type { KitFieldProps } from "./kitFieldProps.types";

type CountFieldValue = number | null | undefined;

/** Paths of `Values` that hold a whole-number count. */
export type CountFieldPath<Values> = FieldPath<Values, CountFieldValue, number>;

export type CountFieldProps<Name extends string> = KitFieldProps<
    Name,
    HTMLInputElement,
    number
> & {
    readOnly?: boolean;
    size?: InputSize;
    min?: number;
};

/**
 * Whole-number stepper field bound to a typed Formik path. The control only
 * ever reports numbers, so the path needs no empty member; an empty value
 * renders a blank input with the steppers disabled.
 */
export const CountField = <
    Values,
    Name extends CountFieldPath<Values> = CountFieldPath<Values>,
>(
    props: CountFieldProps<Name>,
) => {
    const {
        name,
        ref,
        disabled,
        readOnly,
        required,
        onValueChange,
        size,
        min,
    } = props;

    return (
        <FormikField<CountFieldValue> name={name} {...pickChromeProps(props)}>
            {({ field, helpers, invalid }) => (
                <CountInput
                    ref={ref}
                    name={name}
                    value={field.value ?? undefined}
                    onValueChange={(value) => {
                        void helpers.setValue(value);
                        onValueChange?.(value);
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    invalid={invalid}
                    disabled={disabled}
                    readOnly={readOnly}
                    required={required}
                    aria-labelledby={props["aria-labelledby"]}
                    size={size}
                    min={min}
                />
            )}
        </FormikField>
    );
};
